import {
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  signal,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import {
  ChronoUnit,
  Duration,
  Instant, LocalDate,
  LocalDateTime,
  LocalTime,
  Period,
  TemporalAmount,
  ZonedDateTime,
  ZoneId,
  ZoneOffset
} from "@js-joda/core";
import { TranslocoPipe } from "@jsverse/transloco";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { NgTemplateOutlet } from "@angular/common";
import { getMonthGridDays, getWeekdays } from "../../calendar/calendar.utils";

export function countDays(from: Instant, to: Instant): number {
  // This function is slightly wrong because it will return 0 when there are daylight savings change inbetween the from and to
  const fromUTC = LocalDateTime.ofInstant(from, ZoneOffset.UTC);
  const toUTC = LocalDateTime.ofInstant(to, ZoneOffset.UTC);

  return ChronoUnit.DAYS.between(fromUTC, toUTC);
}

export function evaluateDateRange(value: DateRange): [Instant, Instant] {
  if (Array.isArray(value)) {
    return value;
  }

  // Apply duration/period to current date
  const now = ZonedDateTime.now();
  return [
    now.minus(value).toInstant(),
    now.toInstant(),
  ];
}

export type DateRange = [Instant, Instant] | Duration;

// `to` is null while the user is still picking the second date in the calendar.
export type CalendarRange = [LocalDate, LocalDate | null];

interface Intervals {
  translationKey: string;
  value: TemporalAmount;
}

export const intervals: Intervals[] = [
  { translationKey: 'period.yesterday', value: Duration.ofHours(24) },
  { translationKey: 'period.last_seven_days', value: Duration.ofDays(7) },
  { translationKey: 'period.last_thirty_days', value: Duration.ofDays(30) },
  { translationKey: 'period.last_six_months', value: Period.ofMonths(6) },
] as const;

type ChangeFn = (value: DateRange | null) => void;

// Phones (Material's XSmall breakpoint) get the single-calendar, pill layout.
const MOBILE_MEDIA_QUERY = "(max-width: 599px)";

@Component({
  selector: "period-select",
  templateUrl: "./period-select.component.html",
  styleUrls: ["./period-select.component.css"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslocoPipe,
    MatMenuModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    NgTemplateOutlet,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PeriodSelectComponent),
      multi: true,
    },
  ],
})
export class PeriodSelectComponent implements ControlValueAccessor {
  // Predefined options shown beside the calendar.
  protected readonly predefinedIntervals = intervals;

  // The currently selected predefined interval, if any.
  protected readonly interval = signal<Intervals | undefined>(undefined);

  private readonly date = LocalDate.now();

  // The range currently being picked / selected in the calendar.
  // `to` is null after the first click, while the user picks the second date.
  protected readonly calendarRange = signal<CalendarRange | null>(null);

  // ---- Calendar view state ----
  // Left calendar month; seeded from `date` but navigable with prev/next.
  // The right calendar always shows the following month.
  protected readonly viewMonth = signal<LocalDate>(this.date);
  protected readonly rightMonth = computed(() => this.viewMonth().plusMonths(1));
  protected readonly weekdays = getWeekdays();
  protected readonly leftGrid = computed(() => getMonthGridDays(this.viewMonth()));
  protected readonly rightGrid = computed(() => getMonthGridDays(this.rightMonth()));

  // Day under the cursor, used to preview the range while `to` is unset.
  protected readonly hovered = signal<LocalDate | null>(null);

  // Time applied to the `from` date (start) and the `to` date (end). "HH:mm".
  protected readonly startTime = signal("00:00");
  protected readonly endTime = signal("23:59");

  // Tracks small viewports so the dropdown can switch to a single-calendar,
  // pill-based layout that fits on screen without scrolling.
  protected readonly isMobile = signal(false);

  protected isDisabled = false;

  // Trigger label: a formatted custom range when one is selected, otherwise
  // the template falls back to the predefined interval's translation.
  protected readonly selectedLabel = computed(() => {
    const range = this.calendarRange();
    if (range && range[1]) {
      return `${this.formatDay(range[0])} – ${this.formatDay(range[1])}`;
    }

    return null;
  });

  // Normalized bounds so highlighting works regardless of pick order.
  private readonly bounds = computed(() => {
    const range = this.calendarRange();
    if (!range) {
      return null;
    }

    const [from, rawTo] = range;
    // While picking the second date, preview the range up to the hovered day.
    const to = rawTo ?? this.hovered();
    if (!to) {
      return { from, to: null };
    }

    return from.isAfter(to) ? { from: to, to: from } : { from, to };
  });

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  // Present only on desktop, where the picker lives in a menu.
  private readonly menuTrigger = viewChild(MatMenuTrigger);
  // Present only on mobile, where the picker lives in a dialog.
  private mobileDialogRef?: MatDialogRef<unknown>;

  private onChange: ChangeFn;
  private onTouched: () => void;

  constructor() {
    // Self-contained mobile detection: watch the media query directly and mirror
    // it into a signal, so the component does not depend on any layout service.
    const media = window.matchMedia(MOBILE_MEDIA_QUERY);
    this.isMobile.set(media.matches);

    const onChange = (event: MediaQueryListEvent) => this.isMobile.set(event.matches);
    media.addEventListener("change", onChange);
    this.destroyRef.onDestroy(() => media.removeEventListener("change", onChange));
  }

  // ---- ControlValueAccessor ----
  writeValue(value: DateRange | null): void {
    if (Array.isArray(value)) {
      // Custom range: highlight it in the calendar and drop the predefined selection.
      const zone = ZoneId.systemDefault();
      const fromDt = LocalDateTime.ofInstant(value[0], zone);
      const toDt = LocalDateTime.ofInstant(value[1], zone);
      const from = fromDt.toLocalDate();
      const to = toDt.toLocalDate();
      this.interval.set(undefined);
      this.calendarRange.set([from, to]);
      this.startTime.set(this.formatTime(fromDt.toLocalTime()));
      this.endTime.set(this.formatTime(toDt.toLocalTime()));
      this.viewMonth.set(from.withDayOfMonth(1));
      return;
    }

    // Predefined interval (or nothing): highlight the matching option, clear the calendar.
    this.interval.set(value ? intervals.find(x => (x.value as Duration | Period).equals(value)) : undefined);
    this.calendarRange.set(null);
  }

  registerOnChange(fn: ChangeFn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  protected monthLabel(month: LocalDate): string {
    return new Date(month.year(), month.monthValue() - 1, 1)
      .toLocaleDateString(undefined, {month: "long", year: "numeric"});
  }

  // On mobile the picker opens in a dialog instead of a menu, so it is not
  // cramped and never overflows the screen.
  protected openMobileDialog(template: TemplateRef<unknown>) {
    this.mobileDialogRef = this.dialog.open(template, {
      panelClass: "period-picker-dialog",
      maxWidth: "96vw",
      autoFocus: false,
    });
    this.mobileDialogRef.afterClosed().subscribe(() => (this.mobileDialogRef = undefined));
  }

  protected selectInterval(selected: Intervals) {
    this.interval.set(selected);
    this.calendarRange.set(null);
    this.closeOverlay();

    // @ts-expect-error TemporalAmount is a valid DateRange at runtime.
    this.emitValue(selected.value);
  }

  protected previousMonth() {
    this.viewMonth.update(month => month.minusMonths(1));
  }

  protected nextMonth() {
    this.viewMonth.update(month => month.plusMonths(1));
  }

  protected isCurrentMonth(day: LocalDate, month: LocalDate) {
    return day.year() === month.year() && day.monthValue() === month.monthValue();
  }

  // A committed endpoint (rendered solid).
  protected isSelectedEndpoint(day: LocalDate) {
    const range = this.calendarRange();
    if (!range) {
      return false;
    }

    const [from, to] = range;
    return day.equals(from) || (!!to && day.equals(to));
  }

  // The hovered end while `to` is unset (rendered lighter, not yet committed).
  protected isPreviewEndpoint(day: LocalDate) {
    const range = this.calendarRange();
    const hovered = this.hovered();
    if (!range || range[1] || !hovered) {
      return false;
    }

    return day.equals(hovered) && !day.equals(range[0]);
  }

  protected isRangeStart(day: LocalDate) {
    const bounds = this.bounds();
    return this.hasRange(bounds) && day.equals(bounds!.from);
  }

  protected isRangeEnd(day: LocalDate) {
    const bounds = this.bounds();
    return this.hasRange(bounds) && day.equals(bounds!.to!);
  }

  protected isInRange(day: LocalDate) {
    const bounds = this.bounds();
    if (!this.hasRange(bounds)) {
      return false;
    }

    return day.isAfter(bounds!.from) && day.isBefore(bounds!.to!);
  }

  protected handleDaySelected(day: LocalDate) {
    const current = this.calendarRange();

    // Start a fresh range when nothing is in progress or the previous one is complete.
    // Only one endpoint is chosen here, so do not emit yet.
    if (!current || current[1]) {
      this.calendarRange.set([day, null]);
      return;
    }

    // Second click completes the range; order the endpoints so `from <= to`.
    const from = current[0];
    const [rangeFrom, rangeTo] = day.isBefore(from) ? [day, from] : [from, day];
    this.calendarRange.set([rangeFrom, rangeTo]);

    // A valid range is now selected: drop the predefined highlight and emit it.
    this.interval.set(undefined);
    this.emitValue(this.rangeToValue(rangeFrom, rangeTo));
    // Dismiss the mobile dialog once the range is complete; the desktop menu
    // stays open so the picked range remains visible in the calendar.
    this.mobileDialogRef?.close();
  }

  private emitValue(value: DateRange) {
    this.onChange(value);
    this.onTouched();
  }

  private closeOverlay() {
    this.menuTrigger()?.closeMenu();
    this.mobileDialogRef?.close();
  }

  // Re-emit the range with the current times whenever a time input changes.
  protected setStartTime(value: string) {
    this.startTime.set(value || "00:00");
    this.emitCurrentRange();
  }

  protected setEndTime(value: string) {
    this.endTime.set(value || "23:59");
    this.emitCurrentRange();
  }

  private emitCurrentRange() {
    const range = this.calendarRange();
    if (range && range[1]) {
      this.emitValue(this.rangeToValue(range[0], range[1]));
    }
  }

  private rangeToValue(from: LocalDate, to: LocalDate): [Instant, Instant] {
    const zone = ZoneId.systemDefault();

    return [
      // The back-end does not support Instants without milliseconds.
      // It only supports 1 to 3 digits for milliseconds
      // Therefore, here we have to add 1ms to the start and 999 to the end
      from.atTime(LocalTime.parse(this.startTime())).atZone(zone).toInstant().plusMillis(1),
      to.atTime(LocalTime.parse(this.endTime())).atZone(zone).toInstant().plusMillis(999),
    ];
  }

  private formatTime(time: LocalTime): string {
    return `${time.hour().toString().padStart(2, "0")}:${time.minute().toString().padStart(2, "0")}`;
  }

  protected formatDay(day: LocalDate): string {
    return new Date(day.year(), day.monthValue() - 1, day.dayOfMonth())
      .toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
  }

  private hasRange(bounds: {from: LocalDate; to: LocalDate | null} | null): boolean {
    return !!bounds?.to && !bounds.from.equals(bounds.to);
  }
}